<template>
  <v-dialog :value="value" max-width="560" :fullscreen="isMobile" scrollable @input="$emit('input', $event)">
    <div class="ad" @keydown.ctrl.enter.prevent="save" @keydown.meta.enter.prevent="save">
      <!-- context -->
      <header class="ad__head">
        <span class="ad__ctx-ico"><v-icon small color="primary">{{ entityType === 'lead' ? 'mdi-account-outline' : 'mdi-briefcase-outline' }}</v-icon></span>
        <div class="ad__ctx">
          <div class="ad__ctx-title">{{ ctxTitle }}</div>
          <div class="ad__ctx-sub">
            <span>{{ entityType === 'lead' ? $t('crmLead') : $t('crmDeal') }}</span>
            <template v-if="ctxSub"> · {{ ctxSub }}</template>
            <a v-if="phone" :href="'tel:' + phone" class="ad__tel" @click="onDial"><v-icon x-small>mdi-phone</v-icon>{{ phone }}</a>
          </div>
        </div>
        <button type="button" class="ad__close" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></button>
      </header>

      <div class="ad__body">
        <div class="ad__seg">
          <button type="button" :class="{ on: mode === 'done' }" @click="mode = 'done'">
            <v-icon small>mdi-check-circle-outline</v-icon>{{ $t('crmModeDone') }}
          </button>
          <button type="button" :class="{ on: mode === 'schedule' }" @click="mode = 'schedule'">
            <v-icon small>mdi-calendar-clock-outline</v-icon>{{ $t('crmModeSchedule') }}
          </button>
        </div>

        <div class="ad__types" role="radiogroup">
          <button
            v-for="t in types"
            :key="t"
            type="button"
            role="radio"
            :aria-checked="f.type === t"
            class="ad__type"
            :class="{ on: f.type === t }"
            @click="setType(t)"
          >
            <v-icon small>{{ icons[t] }}</v-icon><span>{{ $t('crmAct_' + t) }}</span>
          </button>
        </div>

        <v-alert v-if="error" type="error" text dense class="mb-4">{{ error }}</v-alert>

        <!-- DONE: what happened -->
        <template v-if="mode === 'done'">
          <div v-if="outcomes.length" class="ad__field">
            <div class="ad__label">{{ $t('crmOutcome') }}</div>
            <div class="ad__chips">
              <button
                v-for="o in outcomes"
                :key="o.key"
                type="button"
                class="ad__chip"
                :class="{ on: outcome === o.key, ['ad__chip--' + o.tone]: true }"
                @click="pickOutcome(o)"
              >
                <v-icon x-small>{{ o.icon }}</v-icon>{{ o.label }}
              </button>
            </div>
          </div>
          <!-- call details (doc §6.10.1): direction + talk time -->
          <div v-if="f.type === 'call'" class="ad__call ad__field">
            <div class="ad__dir">
              <button type="button" :class="{ on: f.direction === 'outbound' }" @click="f.direction = 'outbound'"><v-icon x-small>mdi-phone-outgoing</v-icon>{{ $t('crmDir_outbound') }}</button>
              <button type="button" :class="{ on: f.direction === 'inbound' }" @click="f.direction = 'inbound'"><v-icon x-small>mdi-phone-incoming</v-icon>{{ $t('crmDir_inbound') }}</button>
            </div>
            <div class="ad__dur">
              <v-icon x-small>mdi-timer-outline</v-icon>
              <span>{{ $t('crmDuration') }}</span>
              <input v-model.number="f.dur_min" type="number" min="0" dir="ltr" :aria-label="$t('crmMinutes')">
              <i>:</i>
              <input v-model.number="f.dur_sec" type="number" min="0" max="59" dir="ltr" :aria-label="$t('crmSeconds')">
              <button v-for="m in [1, 3, 5, 10]" :key="m" type="button" class="ad__durq" @click="f.dur_min = m; f.dur_sec = 0">{{ m }}′</button>
            </div>
          </div>
          <v-textarea
            ref="result"
            v-model="f.result"
            :label="f.type === 'note' ? $t('crmNote') : $t('crmActResultHint')"
            outlined
            dense
            hide-details
            rows="3"
            auto-grow
            class="ad__field"
          />

          <div class="ad__next" :class="{ on: withNext }">
            <v-switch v-model="withNext" inset dense hide-details :label="$t('crmPlanNext')" class="ad__switch" />
            <div v-if="withNext" class="ad__next-body">
              <div class="ad__chips">
                <button v-for="q in quick" :key="q.key" type="button" class="ad__chip" :class="{ on: nextQuick === q.key }" @click="setNext(q)">{{ q.label }}</button>
              </div>
              <div class="ad__row2">
                <date-picker
                  v-model="f.next_at"
                  type="datetime"
                  format="YYYY-MM-DD HH:mm:ss"
                  display-format="jYYYY/jMM/jDD HH:mm"
                  :locale="dateLocale"
                  :placeholder="$t('crmNextFollowUp')"
                  @change="nextQuick = null"
                />
                <v-select v-model="f.next_type" :items="typeItems" :label="$t('crmActType')" outlined dense hide-details />
              </div>
              <v-text-field v-model="f.next_title" :label="$t('crmNextTitle')" :placeholder="defaultNextTitle" outlined dense hide-details persistent-placeholder />
            </div>
          </div>
        </template>

        <!-- SCHEDULE: plan it -->
        <template v-else>
          <v-text-field v-model="f.title" :label="$t('crmActTitle')" :placeholder="$t('crmAct_' + f.type)" outlined dense hide-details persistent-placeholder class="ad__field" autofocus />
          <div class="ad__field">
            <div class="ad__label">{{ $t('crmWhen') }}</div>
            <div class="ad__chips">
              <button v-for="q in quick" :key="q.key" type="button" class="ad__chip" :class="{ on: dueQuick === q.key }" @click="setDue(q)">{{ q.label }}</button>
            </div>
          </div>
          <div class="ad__row2 ad__field">
            <date-picker
              v-model="f.due_at"
              type="datetime"
              format="YYYY-MM-DD HH:mm:ss"
              display-format="jYYYY/jMM/jDD HH:mm"
              :locale="dateLocale"
              :placeholder="$t('crmDueAt')"
              @change="dueQuick = null"
            />
            <v-select v-model="f.remind" :items="remindItems" :label="$t('crmRemind')" outlined dense hide-details prepend-inner-icon="mdi-bell-outline" />
          </div>
          <div class="ad__row2 ad__field">
            <crm-user-picker v-model="f.owner_user_id" title="crmOwner" />
            <v-select v-model="f.priority" :items="priorityItems" :label="$t('crmPriority')" outlined dense hide-details />
          </div>
          <v-textarea v-model="f.content" :label="$t('crmDescription')" outlined dense hide-details rows="2" auto-grow />
        </template>
      </div>

      <footer class="ad__foot">
        <v-btn color="primary" depressed class="app-form-action-btn" :loading="saving" @click="save">
          {{ mode === 'done' ? $t('crmSaveLog') : $t('crmSaveSchedule') }}
        </v-btn>
        <v-btn color="grey" outlined class="app-form-action-btn" @click="$emit('input', false)">{{ $t('crmCancel') }}</v-btn>
        <v-spacer />
        <span class="ad__summary">
          <template v-if="summary"><v-icon x-small>mdi-calendar-clock-outline</v-icon>{{ summary }}</template>
          <kbd v-if="!isMobile">Ctrl ↵</kbd>
        </span>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
import CrmUserPicker from './CrmUserPicker'
import moment from 'jalali-moment'
import api, { crmError } from '../crmApi'
import { crm, ACTIVITY_ICONS, PRIORITIES, memberOptions, fullName, dealCustomer, fmtDate } from '../crmStore'

const TYPES = ['call', 'meeting', 'sms', 'email', 'visit', 'note', 'task']
const SQL = 'YYYY-MM-DD HH:mm:ss'

function at (days, hour, minute = 0) {
  return moment().add(days, 'days').hour(hour).minute(minute).second(0).locale('en').format(SQL)
}

export default {
  name: 'CrmActivityDialog',
  components: { CrmUserPicker },
  props: {
    value: Boolean,
    entityType: String,
    record: { type: Object, default: null },
    defaultType: { type: String, default: 'call' },
    defaultMode: { type: String, default: 'done' }
  },
  data: () => ({ f: {}, mode: 'done', outcome: null, withNext: true, nextQuick: 'tomorrow', dueQuick: 'tomorrow', saving: false, error: '' }),
  computed: {
    types () { return TYPES },
    icons () { return ACTIVITY_ICONS },
    dateLocale () { return this.$i18n.locale === 'en' ? 'en' : 'fa' },
    typeItems () { return TYPES.filter(t => t !== 'note').map(t => ({ value: t, text: this.$t('crmAct_' + t) })) },
    ownerItems () { return memberOptions() },
    priorityItems () { return PRIORITIES.map(p => ({ value: p, text: this.$t('crmPriority_' + p) })) },
    ctxTitle () {
      const r = this.record || {}
      return r.title || fullName(r) || r.company_name || (r.id ? '#' + r.id : '')
    },
    ctxSub () {
      const r = this.record || {}
      return this.entityType === 'lead' ? (fullName(r) ? r.company_name : '') : dealCustomer(r)
    },
    phone () {
      const r = this.record || {}
      return r.mobile || r.phone || (r.person && (r.person.mobile || r.person.phone)) || ''
    },
    quick () {
      const h = moment().hour()
      const list = []
      if (h < 16) list.push({ key: 'today', label: this.$t('crmTodayEvening'), v: () => at(0, 17) })
      list.push(
        { key: 'tomorrow', label: this.$t('crmTomorrow10'), v: () => at(1, 10) },
        { key: 'd3', label: this.$t('crmIn3Days'), v: () => at(3, 10) },
        { key: 'week', label: this.$t('crmNextWeek'), v: () => at(7, 10) },
        { key: 'month', label: this.$t('crmNextMonth'), v: () => at(30, 10) }
      )
      return list
    },
    outcomes () {
      const t = this.f.type
      if (t === 'call') {
        return [
          { key: 'answered', icon: 'mdi-phone-check-outline', tone: 'ok', label: this.$t('crmOut_answered') },
          { key: 'no_answer', icon: 'mdi-phone-missed-outline', tone: 'warn', label: this.$t('crmOut_no_answer'), next: 'tomorrow' },
          { key: 'busy', icon: 'mdi-phone-cancel-outline', tone: 'warn', label: this.$t('crmOut_busy'), next: 'today' },
          { key: 'callback', icon: 'mdi-phone-return-outline', tone: 'info', label: this.$t('crmOut_call_back'), next: 'tomorrow' },
          { key: 'voicemail', icon: 'mdi-voicemail', tone: 'warn', label: this.$t('crmOut_voicemail'), next: 'tomorrow' },
          { key: 'rejected', icon: 'mdi-phone-hangup-outline', tone: 'bad', label: this.$t('crmOut_rejected'), next: 'd3' },
          { key: 'wrong_number', icon: 'mdi-phone-off-outline', tone: 'bad', label: this.$t('crmOut_wrong') }
        ]
      }
      if (t === 'meeting' || t === 'visit') {
        return [
          { key: 'positive', icon: 'mdi-thumb-up-outline', tone: 'ok', label: this.$t('crmOut_positive') },
          { key: 'needs_offer', icon: 'mdi-file-document-edit-outline', tone: 'info', label: this.$t('crmOut_needs_offer'), next: 'd3' },
          { key: 'thinking', icon: 'mdi-timer-sand', tone: 'warn', label: this.$t('crmOut_thinking'), next: 'week' },
          { key: 'negative', icon: 'mdi-thumb-down-outline', tone: 'bad', label: this.$t('crmOut_negative') }
        ]
      }
      if (t === 'sms' || t === 'email') {
        return [
          { key: 'sent', icon: 'mdi-send-check-outline', tone: 'ok', label: this.$t('crmOut_sent') },
          { key: 'replied', icon: 'mdi-reply-outline', tone: 'info', label: this.$t('crmOut_replied') }
        ]
      }
      return []
    },
    defaultNextTitle () { return this.$t('crmFollowUp') + ' — ' + this.ctxTitle },
    summary () {
      const v = this.mode === 'done' ? (this.withNext && this.f.next_at) : this.f.due_at
      return v ? this.$t('crmSummaryNext', { at: fmtDate(v, true) }) : ''
    },
    remindItems () {
      return [
        { value: null, text: this.$t('crmNoRemind') },
        { value: 15, text: this.$t('crmRemind15') },
        { value: 60, text: this.$t('crmRemind60') },
        { value: 1440, text: this.$t('crmRemind1d') }
      ]
    }
  },
  watch: {
    value: { immediate: true, handler (v) { if (v) this.reset() } }
  },
  methods: {
    reset () {
      const me = crm.me ? crm.me.user_id : null
      this.mode = this.defaultMode === 'schedule' ? 'schedule' : 'done'
      this.f = {
        type: TYPES.includes(this.defaultType) ? this.defaultType : 'call',
        title: '',
        result: '',
        content: '',
        due_at: at(1, 10),
        remind: 15,
        owner_user_id: (this.record && this.record.owner_user_id) || me,
        priority: 'normal',
        next_at: at(1, 10),
        next_type: 'call',
        next_title: '',
        direction: 'outbound',
        dur_min: null,
        dur_sec: null
      }
      this.outcome = null
      this.nextQuick = 'tomorrow'
      this.dueQuick = 'tomorrow'
      // a note rarely needs a follow-up; anything else does unless one is already planned
      this.withNext = this.f.type !== 'note'
      this.error = ''
    },
    setType (t) {
      this.f.type = t
      this.outcome = null
      if (t === 'note') this.withNext = false
      if (t !== 'note' && t !== 'task') this.f.next_type = t === 'sms' || t === 'email' ? 'call' : t
    },
    pickOutcome (o) {
      const same = this.outcome === o.key
      this.outcome = same ? null : o.key
      if (!same && o.next) {
        const q = this.quick.find(x => x.key === o.next) || this.quick[0]
        this.withNext = true
        this.setNext(q)
      }
      if (!same && o.tone === 'bad') this.withNext = false
      this.$nextTick(() => this.$refs.result && this.$refs.result.focus())
    },
    setNext (q) { this.nextQuick = q.key; this.f.next_at = q.v() },
    setDue (q) { this.dueQuick = q.key; this.f.due_at = q.v() },
    onDial () {
      this.mode = 'done'
      this.setType('call')
    },
    remindAt (when, minutes) {
      if (!when || !minutes) return null
      return moment(when, SQL).subtract(minutes, 'minutes').locale('en').format(SQL)
    },
    outcomeLabel () {
      const o = this.outcomes.find(x => x.key === this.outcome)
      return o ? o.label : ''
    },
    async save () {
      if (this.saving) return
      this.saving = true
      this.error = ''
      const base = { entity_type: this.entityType, entity_id: this.record && this.record.id }
      try {
        let act
        if (this.mode === 'done') {
          const isCall = this.f.type === 'call'
          const result = isCall ? this.f.result : [this.outcomeLabel(), this.f.result].filter(Boolean).join(' — ')
          const duration = (Number(this.f.dur_min) || 0) * 60 + (Number(this.f.dur_sec) || 0)
          act = await api.createActivity({
            ...base,
            type: this.f.type,
            title: this.outcomeLabel() ? this.$t('crmAct_' + this.f.type) + ': ' + this.outcomeLabel() : this.$t('crmAct_' + this.f.type),
            status: 'done',
            result,
            content: this.f.type === 'note' ? this.f.result : undefined,
            ...(isCall ? { outcome: this.outcome || undefined, direction: this.f.direction, duration_seconds: duration || undefined } : {})
          })
          if (this.withNext && this.f.next_at) {
            await api.createActivity({
              ...base,
              type: this.f.next_type,
              title: this.f.next_title || this.defaultNextTitle,
              status: 'open',
              due_at: this.f.next_at,
              remind_at: this.remindAt(this.f.next_at, 15),
              owner_user_id: this.f.owner_user_id
            })
          }
        } else {
          if (!this.f.due_at) { this.error = this.$t('crmDueRequired'); return }
          act = await api.createActivity({
            ...base,
            type: this.f.type,
            title: this.f.title || this.$t('crmAct_' + this.f.type),
            content: this.f.content,
            status: 'open',
            due_at: this.f.due_at,
            remind_at: this.remindAt(this.f.due_at, this.f.remind),
            owner_user_id: this.f.owner_user_id,
            priority: this.f.priority
          })
        }
        this.$emit('saved', act)
        this.$emit('input', false)
      } catch (e) {
        this.error = crmError(e).message
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.ad { background: #fff; border-radius: 6px; overflow: hidden; display: flex; flex-direction: column; max-height: 92vh; }
.theme--dark .ad { background: #1e1e1e; }
.ad__head { display: flex; align-items: center; gap: 12px; padding: 14px 18px; border-bottom: 1px solid rgba(15, 23, 42, .07); flex-shrink: 0; }
.ad__ctx-ico { width: 40px; height: 40px; border-radius: 6px; background: rgba(25, 118, 210, .1); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ad__ctx { flex: 1; min-width: 0; }
.ad__ctx-title { font-weight: 700; font-size: .95rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ad__ctx-sub { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; font-size: .76rem; opacity: .75; }
.ad__tel { display: inline-flex; align-items: center; gap: 3px; margin-inline-start: 8px; padding: 1px 8px; border-radius: 6px; background: rgba(15, 157, 118, .12); color: #0f9d76; direction: ltr; text-decoration: none; font-weight: 600; }
.ad__tel ::v-deep .v-icon { color: #0f9d76 !important; }
.ad__close { width: 36px; height: 36px; border-radius: 6px; background: rgba(0, 0, 0, .05); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ad__body { padding: 16px 18px 20px; flex: 1; overflow-y: auto; overflow-x: hidden; }
.ad__seg { display: flex; padding: 3px; background: rgba(127, 127, 127, .1); border-radius: 6px; margin-bottom: 14px; }
.ad__seg button { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px; height: 34px; border-radius: 6px; font-size: .84rem; font-weight: 600; opacity: .65; transition: all .15s; }
.ad__seg button.on { background: #fff; opacity: 1; color: var(--v-primary-base); box-shadow: 0 1px 3px rgba(15, 23, 42, .12); }
.ad__seg button.on ::v-deep .v-icon { color: var(--v-primary-base) !important; }
.theme--dark .ad__seg button.on { background: #333; }
.ad__types { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 18px; }
.ad__type { display: inline-flex; align-items: center; gap: 5px; height: 32px; padding: 0 11px; border-radius: 6px; border: 1px solid rgba(15, 23, 42, .12); font-size: .8rem; transition: all .15s; }
.ad__type:hover { border-color: rgba(25, 118, 210, .5); }
.ad__type.on { border-color: var(--v-primary-base); background: var(--v-primary-base); color: #fff; }
.ad__type.on ::v-deep .v-icon { color: #fff !important; }
.ad__field { margin-bottom: 18px; }
.ad__label { font-size: .76rem; opacity: .65; margin-bottom: 8px; }
.ad__chips { display: flex; flex-wrap: wrap; gap: 6px; }
.ad__chip { display: inline-flex; align-items: center; gap: 4px; height: 30px; padding: 0 11px; border-radius: 6px; border: 1px solid rgba(15, 23, 42, .12); font-size: .78rem; transition: all .15s; }
.ad__chip:hover { border-color: rgba(25, 118, 210, .5); }
.ad__chip.on { background: rgba(25, 118, 210, .1); border-color: var(--v-primary-base); color: var(--v-primary-base); font-weight: 600; }
.ad__chip--ok.on { background: rgba(15, 157, 118, .12); border-color: #0f9d76; color: #0f9d76; }
.ad__chip--warn.on { background: rgba(217, 130, 43, .12); border-color: #d9822b; color: #b86a1a; }
.ad__chip--bad.on { background: rgba(192, 57, 43, .1); border-color: #c0392b; color: #c0392b; }
.ad__chip.on ::v-deep .v-icon { color: inherit !important; }
.ad__call { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.ad__dir { display: inline-flex; padding: 3px; border-radius: 6px; background: rgba(127, 127, 127, .1); }
.ad__dir button { display: inline-flex; align-items: center; gap: 4px; height: 28px; padding: 0 10px; border-radius: 6px; font-size: .76rem; font-weight: 600; opacity: .65; }
.ad__dir button.on { background: #fff; opacity: 1; color: var(--v-primary-base); box-shadow: 0 1px 3px rgba(15, 23, 42, .12); }
.theme--dark .ad__dir button.on { background: #333; }
.ad__dur { display: inline-flex; align-items: center; gap: 5px; font-size: .78rem; }
.ad__dur span { opacity: .7; margin-inline-end: 2px; }
.ad__dur input { width: 44px; height: 30px; border: 1px solid rgba(15, 23, 42, .14); border-radius: 6px; text-align: center; font-size: .82rem; color: inherit; outline: none; -moz-appearance: textfield; }
.ad__dur input:focus { border-color: var(--v-primary-base); }
.ad__dur input::-webkit-inner-spin-button { -webkit-appearance: none; }
.ad__durq { height: 26px; padding: 0 7px; border-radius: 6px; font-size: .72rem; border: 1px solid rgba(15, 23, 42, .1); }
.ad__durq:hover { border-color: var(--v-primary-base); color: var(--v-primary-base); }
.ad__next { border: 1px solid rgba(15, 23, 42, .1); border-radius: 6px; padding: 10px 14px; transition: border-color .15s, background .15s; }
.ad__next.on { border-color: rgba(25, 118, 210, .35); background: rgba(25, 118, 210, .03); }
.ad__switch { margin: 0 !important; }
.ad__next-body { display: flex; flex-direction: column; gap: 14px; margin-top: 12px; }
.ad__row2 { display: grid; grid-template-columns: 1.4fr 1fr; gap: 10px; align-items: center; }
.ad__foot { display: flex; align-items: center; gap: 10px; padding: 12px 18px; background: #fafbfd; border-top: 1px solid rgba(15, 23, 42, .07); flex-shrink: 0; }
.theme--dark .ad__foot { background: #232323; }
.ad__summary { display: inline-flex; align-items: center; gap: 6px; font-size: .74rem; opacity: .7; }
.ad__summary kbd { font-size: .65rem; padding: 0 5px; border-radius: 4px; background: rgba(127, 127, 127, .15); color: inherit; box-shadow: none; }
.ad ::v-deep .v-input { height: auto !important; margin: 0; padding: 0; }
.ad ::v-deep fieldset { border-radius: 6px; }
@media (max-width: 799px) {
  .ad { max-height: none; height: 100%; border-radius: 0; }
  .ad__row2 { grid-template-columns: 1fr; }
  .ad__summary { display: none; }
}
</style>
